import { useQuery } from '@tanstack/react-query'
import { request, requestList, buildQuery, orgBase } from '@/api/http'
import type * as discovery from '@/api/types/discovery'

// ── qtz-readiness /discovery/* ──────────────────────────────────────────────
// qtz-readiness keeps its own copy of qtz-discovery's estate and serves it
// here; the portal never calls qtz-discovery itself. POST /sync asks the
// backend to pull from Discovery now: 202 with the run (status `running`
// unless `wait` is set), 409 SYNC_IN_PROGRESS while a pull is already
// running, 503 DISCOVERY_NOT_CONFIGURED when neither the service nor the
// organisation holds Discovery credentials.

const base = (orgId: string) => `${orgBase(orgId)}/discovery`

// The backend caps an asset page at 500 rows.
export const ASSET_PAGE_SIZE = 500

function listAssets(orgId: string, params?: discovery.ListAssetsParams): Promise<discovery.AssetPage> {
  return request(`${base(orgId)}/assets${buildQuery(params)}`)
}

export const discoveryApi = {
  summary(orgId: string): Promise<discovery.Summary> {
    return request(`${base(orgId)}/summary`)
  },
  systems(orgId: string): Promise<discovery.System[]> {
    return requestList(`${base(orgId)}/systems`)
  },
  system(orgId: string, systemId: string): Promise<discovery.SystemDetail> {
    return request(`${base(orgId)}/systems/${systemId}`)
  },
  sources(orgId: string): Promise<discovery.Source[]> {
    return requestList(`${base(orgId)}/sources`)
  },
  assets: listAssets,
  // Every matching asset, walked page by page; the first page gives the total
  // and the remaining pages load in parallel. Ordering is stable server-side.
  async allAssets(orgId: string, params: Omit<discovery.ListAssetsParams, 'limit' | 'offset'> = {}): Promise<discovery.Asset[]> {
    const first = await listAssets(orgId, { ...params, limit: ASSET_PAGE_SIZE, offset: 0 })
    const pages = Math.max(0, Math.ceil(first.total / ASSET_PAGE_SIZE) - 1)
    const rest = await Promise.all(
      Array.from({ length: pages }, (_, i) => listAssets(orgId, { ...params, limit: ASSET_PAGE_SIZE, offset: (i + 1) * ASSET_PAGE_SIZE }))
    )
    return [first, ...rest].flatMap((page) => page.items ?? [])
  },
  dependencies(orgId: string): Promise<discovery.DependencyReport> {
    return request(`${base(orgId)}/dependencies`)
  },
  syncRuns(orgId: string): Promise<discovery.SyncRun[]> {
    return requestList(`${base(orgId)}/sync-runs`)
  },
  syncRun(orgId: string, runId: number): Promise<discovery.SyncRun> {
    return request(`${base(orgId)}/sync-runs/${runId}`)
  },
  sync(orgId: string, body: discovery.SyncRequest = {}): Promise<discovery.SyncRun> {
    return request(`${base(orgId)}/sync`, { method: 'POST', body: JSON.stringify(body) })
  },
  connection(orgId: string): Promise<discovery.Connection> {
    return request(`${base(orgId)}/connection`)
  },
  updateConnection(orgId: string, body: discovery.ConnectionUpdate): Promise<discovery.Connection> {
    return request(`${base(orgId)}/connection`, { method: 'PUT', body: JSON.stringify(body) })
  },
}

// The vendor components and their PQC readiness, for the Technology
// dependencies card.
export function useDiscoveryDependencies(orgId: string) {
  const enabled = !!orgId
  const q = useQuery({
    queryKey: ['discovery-dependencies', orgId],
    queryFn: () => discoveryApi.dependencies(orgId),
    enabled,
    retry: false,
  })
  return { enabled, ...q }
}
